// The Klaviyo engagement segments (3.2), in words: which segment is one, how
// the Szegmensek page groups them by country, what a row says about its
// numbers, and whether the campaign wizard may offer one.
//
// No React here, so the admin tests run it on Node (tests/admin).

import type { KlaviyoSegment, Segment } from "@/lib/api";
import { count } from "@/components/emc/format";

export const KLAVIYO_SOURCE = "klaviyo";

/** Is this one of Klaviyo's — members from Klaviyo, read-only here? */
export function isKlaviyo(segment: { source?: string | null }): boolean {
  return segment.source === KLAVIYO_SOURCE;
}

function fold(text: string): string {
  return text.normalize("NFD").replace(/\p{M}/gu, "").toLowerCase();
}

export interface CountryGroup {
  country: string;
  rows: KlaviyoSegment[];
}

/** By country (the order the countries are listed in, then the rest by
 * code), each by name; `needle` keeps the rows whose name has it. */
export function groupByCountry(rows: KlaviyoSegment[], order: string[], needle = ""): CountryGroup[] {
  const wanted = fold(needle.trim());
  const groups = new Map<string, KlaviyoSegment[]>();
  for (const row of rows) {
    if (wanted !== "" && !fold(row.name).includes(wanted) && !fold(row.klaviyo_name).includes(wanted)) continue;
    groups.set(row.country, [...(groups.get(row.country) ?? []), row]);
  }
  const rank = (country: string) => {
    const at = order.indexOf(country);
    return at === -1 ? order.length : at;
  };
  return [...groups.entries()]
    .sort(([a], [b]) => rank(a) - rank(b) || a.localeCompare(b))
    .map(([country, members]) => ({
      country,
      rows: [...members].sort((a, b) => fold(a.name).localeCompare(fold(b.name)) || a.segment_id - b.segment_id),
    }));
}

/** "62%" of a whole, or null when there is nothing to divide by. */
function percent(part: number | null, whole: number | null): string | null {
  if (part === null || whole === null || whole <= 0) return null;
  return `${Math.round((part / whole) * 100)}%`;
}

export interface RowWords {
  /** Where it stands: one word for the pill, one phrase for its title. */
  pill: "kész" | "vár" | "megszűnt";
  standing: string;
  /** "gone" and "waiting" are not "ready": no campaign can use it now. */
  tone: "good" | "warn" | "muted";
  inKlaviyo: string;
  /** Of Klaviyo's members, how many are contacts here. */
  found: string;
  /** "12 nincs meg nálunk" — profiles no contact stands for. */
  notFound: string | null;
  /** Of the members here, how many may be mailed; and the share. */
  sendable: string;
}

export function rowWords(row: KlaviyoSegment): RowWords {
  let pill: RowWords["pill"] = "kész";
  let standing = "kész: kampány választhatja";
  let tone: RowWords["tone"] = "good";
  if (row.gone_at !== null) {
    pill = "megszűnt";
    standing = "a Klaviyóban megszűnt — az utolsó tagsága megmaradt, kampány nem választhatja";
    tone = "muted";
  } else if (row.refreshed_at === null) {
    pill = "vár";
    standing = "még nem frissült a Klaviyóból";
    tone = "warn";
  } else if (row.state !== "ready") {
    pill = "vár";
    standing = "a következő frissítésre vár";
    tone = "warn";
  }
  const share = percent(row.sendable_count, row.member_count);
  return {
    pill,
    standing,
    tone,
    inKlaviyo: count(row.klaviyo_count),
    found: count(row.member_count),
    notFound: row.not_found_count ? `${count(row.not_found_count)} nincs meg nálunk` : null,
    sendable: share === null ? count(row.sendable_count) : `${count(row.sendable_count)} (${share})`,
  };
}

export interface CampaignChoice {
  label: string;
  /** Why the wizard cannot offer it now, when it cannot. */
  disabled?: string;
}

/** A segment as the campaign wizard's picker lists it. A Klaviyo one is
 * marked, and offered only once its members are there: a stale or gone one
 * would fail the assembly. */
export function campaignChoice(segment: Segment): CampaignChoice {
  const size = segment.member_count === null ? "" : ` — ${count(segment.member_count)} fő`;
  if (!isKlaviyo(segment)) return { label: `${segment.name}${size}` };
  const label = `${segment.name} · Klaviyo${size}`;
  if (segment.state === "ready") return { label };
  if (segment.state === "stale") return { label, disabled: "a Klaviyóban megszűnt" };
  return { label, disabled: "még nem frissült a Klaviyóból" };
}

/** Our own segments for the Aktív and Archivált tabs; Klaviyo's have their own. */
export function oursOnly<T extends { source?: string | null }>(segments: T[]): T[] {
  return segments.filter((segment) => !isKlaviyo(segment));
}
